import React, { useState, useEffect, useRef } from "react";
import { TabView, TabPanel } from "primereact/tabview";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { MultiSelect } from "primereact/multiselect";
import { Grid } from "@mui/material";
import { v4 as uuid } from "uuid";

import {
  createIncident,
  updateIncident,
  getIncident,
  IncidentCreateUpdateRequest,
  IncidentQuestionAnswer,
  IncidentDocument,
} from "../../../service/incidentService";
import { getIncidentTypes, getIncidentType } from "../../../service/incidentTypeService";
import { s3UploadFile } from "../../../service/s3Service";
import { downloadFile } from "../../../service/documentsService";
import FormControl from "@mui/joy/FormControl";
import { dropdownViewGet } from "../../../service/api-service";
import GoogleMapsViewV2 from "../../../components/GoogleMapsViewV2";
import dayjs from "dayjs";
import { useSession } from "../../../context/SessionContext";
import useApiService from "../../../hooks/useApiService";
import { incidentType } from "../../../models/incidents.interface";
import { EmployeeDetail } from "../../../models/employee.interface";
import { Select, Textarea } from "../../../components/Shared/Forms";
import { Site } from "../../../models/site.interface";

interface IncidentAddProps {
  modalProps: {
    id?: string;
    type_id?: string;
    onSuccess: () => void;
    toast: any;
    setGlobalLoading: (loading: boolean) => void;
  };
}

interface Employee {
  id: string;
  full_name: string;
}

export default function IncidentAdd({ modalProps }: IncidentAddProps) {
  const { session } = useSession();
  const [activeIndex, setActiveIndex] = useState(0);
  const [sites, setSites] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);

  const minPanelHeight = 300;

  // Incident Tab State
  const [siteId, setSiteId] = useState(session.site_id);
  const [employeeId, setEmployeeId] = useState("");
  const [reportingEmployeeId, setReportingEmployeeId] = useState("");
  const [typeId, setTypeId] = useState("");
  const [description, setDescription] = useState("");
  const [incidentDescription, setIncidentDescription] = useState("");
  const [otherParties, setOtherParties] = useState("");
  const [locationDescription, setLocationDescription] = useState("");
  const [otherEmployeeIds, setOtherEmployeeIds] = useState([]);
  const [createdAt, setCreatedAt] = useState("");

  // Other
  const [location, setLocation] = useState("");
  const [locationLabel, setLocationLabel] = useState("");

  // Questions Tab State
  const [questions, setQuestions] = useState<IncidentQuestionAnswer[]>([]);

  // Download Documents Tab State
  const [typeDocuments, setTypeDocuments] = useState<any[]>([]);

  // Upload Documents Tab State
  const [documents, setDocuments] = useState<IncidentDocument[]>([]);
  const [newDocuments, setNewDocuments] = useState<{ name: string; file: File }[]>([]);
  const [newDocumentName, setNewDocumentName] = useState("");
  const fileNewDocumentRef = useRef<FileUpload>(null);

  const { data: incidentTypesData, loading: loadingIncidentTypes } = useApiService<incidentType[]>({
    url: "incident/type",
    method: "GET",
    apiName: "core",
    cache: {
      enabled: true,
      strategy: "cache-first",
      key: `core::GET::incident/type::client::${session.client_id}`,
    },
  });

  const { data: employeeData, loading: loadingEmployees } = useApiService<EmployeeDetail[]>({
    url: "site-employee",
    method: "GET",
    apiName: "core",
    cache: {
      enabled: true,
      strategy: "cache-first",
      key: `core::GET::site-employee::site::${session.site_id}`,
    },
  });

  const { data: sitesData, loading: loadingSites } = useApiService<Site[]>({
    url: "site-list",
    method: "GET",
    apiName: "core",
    cache: {
      enabled: true,
      strategy: "cache-first",
      key: `core::GET::client::${session.client_id}`,
    },
  });

  // console.log(incidentTypesData);

  useEffect(() => {
    if (modalProps.id) {
      fetchIncidentDetails();
    }
  }, [modalProps.id]);

  useEffect(() => {
    if (typeId) {
      void fetchTypeDetails();
    }
  }, [typeId]);

  const fetchIncidentDetails = async () => {
    setLoading(true);
    try {
      const response = await getIncident(modalProps.id!);
      const incident = response.content;

      // Populate Incident Tab
      setSiteId(incident.site_id || session.site_id);
      setEmployeeId(incident.employee_id);
      setReportingEmployeeId(incident.reporting_employee_id);
      // setLastSetTypeId(incident.type_id);
      setTypeId(incident.type_id);
      setDescription(incident.description);
      setOtherParties(incident.other_parties);
      setOtherEmployeeIds(incident.other_employee_ids || []);
      setCreatedAt(incident.created_at);
      setDocuments(incident.documents || []);
      setLocationDescription(incident.location || "");
      setIncidentDescription(incident.incident_description || "");

      // Split GPS coordinates
      setLocation(incident.gps_coordinates);
      setLocationLabel("");

      // Populate the "frozen" type fields
      setQuestions(incident.questions || []);
      setTypeDocuments(incident.type_documents || []);
    } catch (error) {
      modalProps.toast.current.show({
        severity: "error",
        summary: "Error fetching incident details",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  // const [lastSetTypeId, setLastSetTypeId] = useState<string | null>(null);
  const fetchTypeDetails = async () => {
    // This works, but adds a lot of extra complexity on the backend to handle the edge case of changing the type.
    // So for now, we won't
    // // Only if on create,
    // // OR if edit, then if the typeId has changed from its original
    // // console.log("lastSetTypeId", lastSetTypeId);
    // // console.log("typeId", typeId);
    // // if (!modalProps.id ||
    // //    (modalProps.type_id !== typeId || (!!lastSetTypeId && lastSetTypeId !== typeId)))
    if (!modalProps.id) {
      try {
        const response = await getIncidentType(typeId);
        setQuestions(
          response.content?.questions.map((q: any) => {
            return { question: q.question, answer: "", required: q.required };
          }) || []
        );
        setTypeDocuments(response.content?.documents || []);
        // setLastSetTypeId(typeId);
      } catch (error) {
        modalProps.toast.current.show({
          severity: "error",
          summary: "Error fetching type details",
          life: 3000,
        });
      }
    }
  };

  const handleTypeChange = (selectedTypeId: string) => {
    setTypeId(selectedTypeId);
  };

  const customBase64Uploader = async (event: any) => {
    const uploadedFile = event.files[0] as File;
    if (uploadedFile) {
      setNewDocuments([...newDocuments, { name: newDocumentName, file: uploadedFile }]);
      setNewDocumentName("");
      fileNewDocumentRef.current!.clear();
    }
  };

  const saveIncident = async () => {
    setLoading(true);
    try {
      // Validate required fields
      if (!typeId || !siteId || !employeeId || !description || !location) {
        modalProps.toast.current.show({
          severity: "warn",
          summary: "Please fill in all required fields",
          life: 3000,
        });

        setLoading(false);
        return;
      }

      // Validate required questions
      const requiredQuestions = questions.filter(q => q.required);
      const missingAnswers = requiredQuestions.filter(q => !q.answer);
      if (missingAnswers.length) {
        modalProps.toast.current.show({
          severity: "warn",
          summary: "Please fill in all required questions",
          life: 3000,
        });

        setLoading(false);
        return;
      }

      // Upload new documents
      const uploadedDocs: IncidentDocument[] = [];
      for (const doc of newDocuments) {
        const key = `incident/${modalProps.id || uuid()}/documents/${uuid()}/${doc.file.name}`;
        await s3UploadFile(key, doc.file);
        uploadedDocs.push({ s3_key: key, name: doc.name });
      }

      const incidentData: IncidentCreateUpdateRequest = {
        site_id: siteId,
        employee_id: employeeId,
        type_id: typeId, // will be ignored for updates
        description,
        other_parties: otherParties,
        other_employee_ids: otherEmployeeIds,
        gps_coordinates: location,
        question_answers: questions,
        documents: [...documents, ...uploadedDocs],
        location: locationDescription,
        reporting_employee_id: reportingEmployeeId,
        incident_description: incidentDescription,
      };

      if (modalProps.id) {
        await updateIncident(modalProps.id, incidentData);
        modalProps.toast.current.show({
          severity: "success",
          summary: "Incident Updated Successfully",
          life: 3000,
        });
      } else {
        await createIncident(incidentData);
        modalProps.toast.current.show({
          severity: "success",
          summary: "Incident Created Successfully",
          life: 3000,
        });
      }

      setLoading(false);
      modalProps.onSuccess();
    } catch (error) {
      modalProps.toast.current.show({
        severity: "error",
        summary: "Error saving incident",
        life: 3000,
      });
    }

    setLoading(false);
  };

  const download = async (s3Key: string) => {
    await downloadFile("", s3Key);
  };

  return (
    <React.Fragment>
      <TabView activeIndex={activeIndex} onTabChange={e => setActiveIndex(e.index)}>
        {/* Incident Tab */}
        <TabPanel header="Incident">
          <div style={{ minHeight: minPanelHeight }}>
            <div>
              <div>
                <Select
                  value={typeId}
                  options={
                    incidentTypesData?.map(type => ({ label: type.name, value: type.id })) ?? []
                  }
                  onChange={e => handleTypeChange(e.value)}
                  placeholder="Select Incident Type"
                  className="w-full"
                  required
                  disabled={!!modalProps.id}
                  label={"Incident Type"}
                  loading={loadingIncidentTypes}
                />
              </div>
              <div>
                <Select
                  value={siteId}
                  options={sitesData?.map(site => ({ label: site.name, value: site.id })) ?? []}
                  onChange={e => setSiteId(e.value)}
                  placeholder="Select Site"
                  className="w-full"
                  required
                  label={"Site"}
                  disabled={true}
                  loading={loadingSites}
                />
              </div>
              <div>
                <Select
                  value={reportingEmployeeId}
                  options={
                    employeeData?.map(employee => ({
                      label: employee.full_name,
                      value: employee.id,
                    })) ?? []
                  }
                  onChange={e => setReportingEmployeeId(e.value)}
                  placeholder="Select Employee"
                  className="w-full"
                  required={true}
                  label={"Reporting employee"}
                  loading={loadingEmployees}
                />
              </div>
              <div>
                <Select
                  value={employeeId}
                  options={
                    employeeData?.map(employee => ({
                      label: employee.full_name,
                      value: employee.id,
                    })) ?? []
                  }
                  onChange={e => setEmployeeId(e.value)}
                  placeholder="Select Employee"
                  className="w-full"
                  required
                  label={"Person Involved"}
                  loading={loadingEmployees}
                />
              </div>
              <div>
                <Textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  className="w-full"
                  placeholder={
                    "Describe the injury or loss sustained. For injuries, include body part(s) affected, type (e.g., sprain, cut), and severity. For property or equipment loss, state what was damaged and the extent. If this was a near miss, describe what could have happened if the incident was not avoided."
                  }
                  label={"Loss/Injury Description"}
                  rows={5}
                />
              </div>
              <div>
                <Textarea
                  value={incidentDescription}
                  onChange={e => setIncidentDescription(e.target.value)}
                  className="w-full"
                  placeholder={
                    "Describe what happened, how it happened, and what the outcome was. Include key times, actions taken, and names of any witnesses."
                  }
                  rows={5}
                  label={"Incident Description"}
                />
              </div>
            </div>
          </div>
        </TabPanel>

        {/* Other */}
        <TabPanel header="Details">
          <div style={{ minHeight: minPanelHeight }}>
            <div>
              <div>
                <label>Other Employees</label>
                <MultiSelect
                  value={otherEmployeeIds}
                  options={
                    employeeData?.map(employee => ({
                      label: employee.full_name,
                      value: employee.id,
                    })) ?? []
                  }
                  onChange={e => {
                    setOtherEmployeeIds(e.value);
                  }}
                  placeholder="Select Employees"
                  className="w-full"
                  display="chip"
                  loading={loadingEmployees}
                />
              </div>

              <div>
                <label>Other Parties/Witnesses</label>
                <InputText
                  value={otherParties}
                  onChange={e => setOtherParties(e.target.value)}
                  className="w-full"
                  placeholder="Other people involved"
                />
              </div>

              <div>
                <label>Location description</label>
                <InputText
                  value={locationDescription}
                  onChange={e => setLocationDescription(e.target.value)}
                  className="w-full"
                  placeholder={`e.g. "Boiler Room, Level 3” or “Intersection of High St & Oak Rd"`}
                />
              </div>

              <div>
                <label>
                  Location <span style={{ color: "red" }}>*</span>
                </label>
                <GoogleMapsViewV2
                  locationLabel={locationLabel}
                  locationGps={location}
                  setLocationGps={setLocation}
                  setLocationLabel={setLocationLabel}
                />
              </div>

              {createdAt && (
                <div>
                  <label>Created At</label>
                  <InputText
                    value={dayjs(createdAt).format("YYYY/MM/DD HH:mm")}
                    className="w-full"
                    disabled={true}
                  />
                </div>
              )}
            </div>

            {/*<Grid item xs={6}>*/}
            {/*  <label>GPS Coordinates</label>*/}
            {/*  <InputText*/}
            {/*    value={location}*/}
            {/*    disabled={true}*/}
            {/*    className="w-full"*/}
            {/*  />*/}
            {/*</Grid>*/}
          </div>
        </TabPanel>

        {/* Questions Tab */}
        <TabPanel header="Questions" disabled={!typeId}>
          <div style={{ minHeight: minPanelHeight }}>
            {questions.map((question, index) => (
              <div>
                <label style={{ fontWeight: "bold", marginBottom: 10 }}>
                  {question.question}
                  {!!question.required && <span style={{ color: "red" }}> *</span>}
                </label>
                <InputText
                  value={questions[index]?.answer || ""}
                  onChange={e => {
                    const updatedQuestions = [...questions];
                    updatedQuestions[index] = { ...question, answer: e.target.value };
                    setQuestions(updatedQuestions);
                  }}
                  className="w-full"
                />
              </div>
            ))}
          </div>
        </TabPanel>

        {/* Download Documents Tab */}
        <TabPanel header="Download Documents" disabled={!typeId}>
          <div style={{ minHeight: minPanelHeight }}>
            <ul>
              {typeDocuments.map((doc, index) => (
                <div key={index}>
                  <li>
                    <span
                      className="document-download-link"
                      style={{ flexGrow: 1 }}
                      onClick={() => download(doc.s3_key)}
                    >
                      {doc.name} ({doc.s3_key.slice(doc.s3_key.lastIndexOf("/")).slice(1)})
                    </span>
                  </li>
                </div>
              ))}
            </ul>
          </div>
        </TabPanel>

        {/* Upload Documents Tab */}
        <TabPanel header="Upload Documents" disabled={!typeId}>
          <div style={{ minHeight: minPanelHeight }}>
            {documents.map((d: any, index: number) => {
              return (
                <div key={index}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span
                      className="document-download-link"
                      style={{ flexGrow: 1 }}
                      onClick={() => download(d.s3_key)}
                    >
                      {d.name} ({d.s3_key.slice(d.s3_key.lastIndexOf("/")).slice(1)})
                    </span>
                    <Button
                      icon="pi pi-trash"
                      rounded
                      outlined
                      severity="danger"
                      aria-label="Cancel"
                      text
                      size="small"
                      onClick={() => {
                        setDocuments(documents.filter((_, i) => i !== index));
                      }}
                    />
                  </div>
                </div>
              );
            })}

            {newDocuments.map((d: any, index: number) => {
              return (
                <div key={index}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ flexGrow: 1 }}>{d.name}</span>
                    <Button
                      icon="pi pi-trash"
                      rounded
                      outlined
                      severity="danger"
                      aria-label="Cancel"
                      text
                      size="small"
                      onClick={() => {
                        setNewDocuments(newDocuments.filter((_, i) => i !== index));
                      }}
                    />
                  </div>
                </div>
              );
            })}

            <div style={{ marginTop: 10 }}>
              <div className="flex gap-3 p-fluid">
                {/*<span style={{fontSize: "large"}}>New Document</span>*/}
                <div className="flex-auto">
                  <FormControl sx={{ display: { sm: "flex-column", md: "flex-row" } }}>
                    <InputText
                      placeholder="New Document Name"
                      value={newDocumentName}
                      className="p-inputtext-sm PrimeSmallInput"
                      style={{ marginLeft: 15 }}
                      onChange={e => setNewDocumentName(e.target.value)}
                    />
                  </FormControl>
                </div>
                <div className="flex-auto">
                  <FormControl sx={{ display: { sm: "flex-column", md: "flex-row" }, gap: 2 }}>
                    <FileUpload
                      ref={fileNewDocumentRef}
                      mode="basic"
                      className="PrimeSmallFileUpload"
                      style={{ marginLeft: 5 }}
                      accept="application/msword, application/vnd.ms-excel, application/vnd.ms-powerpoint,text/plain, application/pdf, image/*"
                      auto
                      customUpload
                      chooseLabel="Select Document"
                      uploadHandler={customBase64Uploader}
                      disabled={!newDocumentName.length}
                    />
                  </FormControl>
                </div>
              </div>
            </div>
          </div>
        </TabPanel>
      </TabView>

      <br />
      <br />
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          name="button"
          icon="pi pi-save"
          label="Save"
          className="p-button-outlined"
          loading={loading}
          style={{
            color: "#2dd36f",
          }}
          onClick={() => saveIncident()}
        />
      </div>
    </React.Fragment>
  );
}
